import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const NAV_GROUPS = [
  {
    group: 'Principal',
    items: [
      { to: '/',       label: 'Dashboard', end: true,  icon: DashboardIco },
      { to: '/perfil', label: 'Perfil',    end: false, icon: UserIco      },
    ],
  },
  {
    group: 'Contenido',
    items: [
      { to: '/inteligencia', label: 'Inteligencia', end: false, icon: SparkIco },
      { to: '/generacion',   label: 'Generación',   end: false, icon: LayersIco },
      { to: '/historial',    label: 'Historial',    end: false, icon: HistoryIco },
    ],
  },
  {
    group: 'Comunidad',
    items: [
      { to: '/guias',     label: 'Guías',        end: false, icon: BookIco  },
      { to: '/leads',     label: 'Leads · CRM',  end: false, icon: UsersIco },
      { to: '/comunidad', label: 'Comunidad',     end: false, icon: ChatIco  },
    ],
  },
];

const TITLES: Record<string, string> = {
  '/':             'Dashboard',
  '/inteligencia': 'Inteligencia',
  '/generacion':   'Generación',
  '/historial':    'Historial',
  '/guias':        'Guías',
  '/leads':        'Leads · CRM',
  '/comunidad':    'Comunidad',
  '/perfil':       'Mi perfil',
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const title = TITLES[location.pathname] ?? 'Panel';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = (user?.email ?? 'U')
    .split('@')[0]
    .split(/[._-]/)
    .map((w: string) => w[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');

  const fotoUrl = user?.foto_url ?? user?.avatar_url ?? null;

  return (
    <div className="app">
      {/* ── Sidebar ── */}
      <nav className="sidebar">
        <div className="brand">
          <img src="/logo.png" alt="PsicoDosis" />
          <div>
            <div className="brand-name">Psico<span>Dosis</span></div>
            <div className="brand-sub">Panel interno</div>
          </div>
        </div>

        <div className="nav">
          {NAV_GROUPS.map(({ group, items }) => (
            <div key={group}>
              <div className="nav-label">{group}</div>
              {items.map(({ to, label, end, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                >
                  <Icon />
                  {label}
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        <div className="sidebar-foot">
          <div className="user" onClick={() => navigate('/perfil')} title="Ver mi perfil">
            {fotoUrl ? (
              <img src={fotoUrl} alt={user?.nombre ?? 'Admin'} className="avatar" style={{ objectFit: 'cover' }} />
            ) : (
              <span className="avatar">{initials}</span>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="user-name">{user?.nombre ?? user?.email?.split('@')[0] ?? 'Admin'}</div>
              <div className="user-role">Administradora</div>
            </div>
            <button
              className="btn btn-ghost btn-icon"
              onClick={(e) => { e.stopPropagation(); handleLogout(); }}
              title="Cerrar sesión"
              style={{ width: 32, height: 32, flexShrink: 0 }}
            >
              <LogoutIco />
            </button>
          </div>
        </div>
      </nav>

      {/* ── Main ── */}
      <div className="main">
        <header className="topbar">
          <div>
            <div className="crumb">PsicoDosis · Panel</div>
            <h1 className="serif">{title}</h1>
          </div>
          <div className="topbar-spacer" />
          <div className="search">
            <SearchIco />
            <input placeholder="Buscar temas, contenido, leads…" />
          </div>
          <button className="btn btn-ghost btn-icon" title="Notificaciones" style={{ position: 'relative' }}>
            <BellIco />
            <span style={{ position: 'absolute', top: 8, right: 8, width: 7, height: 7, borderRadius: 99, background: 'var(--accent)', border: '1.5px solid var(--surface)' }} />
          </button>
        </header>

        <div className="scroll">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/* ── Inline SVG icons (stroke, no fill) ── */
function Svg({ children, size = 18 }: { children: React.ReactNode; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}

function DashboardIco() {
  return <Svg><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></Svg>;
}
function SparkIco() {
  return <Svg><path d="M12 3l1.8 4.7L18 9.5l-4.2 1.8L12 16l-1.8-4.7L6 9.5l4.2-1.8z"/><path d="M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"/></Svg>;
}
function LayersIco() {
  return <Svg><path d="M12 3l8 4.5-8 4.5-8-4.5z"/><path d="M4 12l8 4.5L20 12"/><path d="M4 16.5L12 21l8-4.5"/></Svg>;
}
function HistoryIco() {
  return <Svg><path d="M3 12a9 9 0 1 0 3-6.7M3 4v3.5h3.5"/><path d="M12 8v4l3 1.6"/></Svg>;
}
function BookIco() {
  return <Svg><path d="M5 4h9a3 3 0 0 1 3 3v13H8a3 3 0 0 0-3 3z" transform="translate(1 0)"/><path d="M4 4a2 2 0 0 0-2 2v13a3 3 0 0 1 3-3h1"/></Svg>;
}
function UsersIco() {
  return <Svg><circle cx="9" cy="8" r="3.2"/><path d="M3.5 20a5.5 5.5 0 0 1 11 0"/><path d="M16 5.2a3.2 3.2 0 0 1 0 6"/><path d="M17 14.4A5.5 5.5 0 0 1 20.5 20"/></Svg>;
}
function SearchIco() {
  return <Svg><circle cx="11" cy="11" r="7"/><path d="m20 20-3.2-3.2"/></Svg>;
}
function BellIco() {
  return <Svg><path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6z"/><path d="M10 20a2 2 0 0 0 4 0"/></Svg>;
}
function LogoutIco() {
  return <Svg size={16}><path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3"/><path d="M10 17l-5-5 5-5M5 12h11"/></Svg>;
}
function UserIco() {
  return <Svg><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></Svg>;
}
function ChatIco() {
  return <Svg><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></Svg>;
}
